import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiError } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";
import { STAGE_LABEL, field } from "../components/enterprise/EnterpriseNav";

const AddPartner = ({ onAdded }) => {
  const [f, setF] = useState({ name: "", model: "white_label", website: "", contact_name: "", contact_email: "", summary: "" });
  const add = async () => {
    try { const { data } = await api.post("/enterprise/partners", f); toast.success(`${data.name} added as a prospect`); setF({ ...f, name: "", website: "", contact_name: "", contact_email: "", summary: "" }); onAdded(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="panel p-5 mb-8" data-testid="ent-add-partner">
      <p className="label-xs mb-3">Add a potential white-label partner</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <input placeholder="Company name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="ent-add-name" />
        <select value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} className={field} data-testid="ent-add-model">
          <option value="white_label">White-label</option><option value="api">API integration</option><option value="reseller">Reseller</option><option value="referral">Referral</option></select>
        <input placeholder="Website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={field} data-testid="ent-add-website" />
        <input placeholder="Contact name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} className={field} data-testid="ent-add-contact-name" />
        <input placeholder="Contact email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} className={field} data-testid="ent-add-contact-email" />
        <input placeholder="One-line summary" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} className={field} data-testid="ent-add-summary" />
      </div>
      <NeonButton className="mt-3" onClick={add} disabled={f.name.trim().length < 2} data-testid="ent-add-submit"><Plus className="w-3 h-3" /> Add partner</NeonButton>
    </section>
  );
};

const Card = ({ p }) => (
  <Link to={`/app/enterprise/${p.id}`} className="block border border-line bg-white/[0.02] p-3 hover:border-brand transition-colors" data-testid={`ent-card-${p.id}`}>
    <p className="text-sm font-semibold">{p.name}</p>
    <p className="font-mono text-[10px] text-white/45 mt-1 uppercase">{p.model.replace("_", "-")} · score {p.score}/100</p>
    {p.client_workspace_ids?.length > 0 && <p className="text-[11px] text-brand mt-1">{p.client_workspace_ids.length} client merchant(s)</p>}
  </Link>
);

export default function Enterprise() {
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/enterprise/partners").then((r) => setD(r.data)).catch(() => setDenied(true));
  useEffect(() => { load(); }, []);
  if (denied) return <p className="text-white/50" data-testid="enterprise-denied">Page not found.</p>;
  if (!d) return <p className="font-mono text-xs text-white/40">loading…</p>;
  return (
    <div data-testid="enterprise-page">
      <PageHeader eyebrow="// private · only you" title="AEGIS Guard Enterprise">
        <Link to="/app/enterprise/proof" data-testid="ent-open-proof"><NeonButton variant="ghost">Proof & claims (live data)</NeonButton></Link>
      </PageHeader>
      <p className="flex items-center gap-2 text-xs text-white/45 -mt-4 mb-6"><Lock className="w-3 h-3" />Hidden from every merchant, partner and teammate, including Brennan. Every change is written to the audit log.</p>
      <AddPartner onAdded={load} />
      <div className="grid md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ent-pipeline">
        {d.stages.map((s) => (
          <div key={s} className="min-h-[120px]" data-testid={`ent-col-${s}`}>
            <p className="label-xs mb-2">{STAGE_LABEL[s]} <span className="text-white/35">({d.items.filter((p) => p.stage === s).length})</span></p>
            <div className="space-y-2">{d.items.filter((p) => p.stage === s).map((p) => <Card key={p.id} p={p} />)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
